import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface ModeCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  banner?: string;
}

const modeCardStyles = {
  base:
    "group relative flex w-full flex-col gap-3 rounded-xl border px-4 py-4 text-left transition-[border-color,background-color,box-shadow] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  selected:
    "border-primary/40 bg-primary/[0.04] shadow-sm ring-1 ring-primary/15",
  unselected:
    "border-border bg-surface hover:border-primary/20 hover:bg-muted/40",
  iconWrap:
    "flex size-11 shrink-0 items-center justify-center rounded-lg transition-colors duration-200",
  iconSelected: "bg-primary/10 text-primary",
  iconUnselected:
    "bg-muted text-text-muted group-hover:text-text-primary",
  indicator:
    "flex size-5 shrink-0 items-center justify-center rounded-full border transition-all duration-200",
  indicatorSelected: "border-primary bg-primary text-primary-foreground",
  indicatorUnselected: "border-border bg-background",
  title: "text-sm font-semibold leading-tight poppins",
  titleSelected: "text-primary",
  titleUnselected: "text-text-primary",
  banner:
    "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide poppins",
  bannerSelected: "bg-secondary text-primary",
  bannerUnselected: "bg-secondary/15 text-primary",
  description: "text-xs leading-relaxed text-text-muted mulish",
} as const;

export function ModeCard({
  icon,
  title,
  description,
  selected,
  onClick,
  banner,
}: ModeCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        modeCardStyles.base,
        selected ? modeCardStyles.selected : modeCardStyles.unselected,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={cn(
            modeCardStyles.iconWrap,
            selected ? modeCardStyles.iconSelected : modeCardStyles.iconUnselected,
          )}
        >
          {icon}
        </div>
        <span
          aria-hidden
          className={cn(
            modeCardStyles.indicator,
            selected
              ? modeCardStyles.indicatorSelected
              : modeCardStyles.indicatorUnselected,
          )}
        >
          {selected && <Check className="size-3 stroke-[3]" />}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <p
            className={cn(
              modeCardStyles.title,
              selected ? modeCardStyles.titleSelected : modeCardStyles.titleUnselected,
            )}
          >
            {title}
          </p>
          {banner && (
            <span
              className={cn(
                modeCardStyles.banner,
                selected ? modeCardStyles.bannerSelected : modeCardStyles.bannerUnselected,
              )}
            >
              {banner}
            </span>
          )}
        </div>
        <p className={modeCardStyles.description}>{description}</p>
      </div>
    </button>
  );
}
